import { LogOut, MessageSquarePlus } from "lucide-react";
import { useState } from "react";
import { Link } from "@/lib/nav";

import { BrandIcon, BrandWordmark } from "@buildpy/ui/brand-logo";
import FeedbackDialog from "@/components/feedback-dialog";
import RailIconButton from "@/components/rail-icon-button";
import { cn } from "@/lib/utils";
import { Member, ROLE_LABEL } from "@buildpy/core/modules/org/types";
import { PLAN_LABEL, type PlanName } from "@buildpy/core/modules/plan/types";
import AppNavList, { NavBadge } from "@/shell/app-nav-list";

interface Props {
  member: Member;
  /**
   * 로그아웃 서버 액션. 레일이 직접 import 하지 않는다 — session 모듈은
   * next/headers 를 쓰는 서버 전용이라 클라이언트 번들에 들어가면 안 된다.
   * 서버 레이아웃이 액션 참조를 내려준다.
   */
  onLogout: () => Promise<void>;
  /** 로고 아래 시공사 이름 — 못 읽으면 비워 둔다. */
  orgName?: string | null;
  /** 시공사 이름 아래 요금제 이름(무료·초대·Pro·Max) — 못 읽으면 비워 둔다. */
  plan?: PlanName | null;
  badges?: Record<string, NavBadge>;
  className?: string;
  onNavigate?: () => void;
}

// 레일과 모바일 메뉴 바닥에 같은 모양으로 붙는 사용자 줄 — 이름 · 역할 · 의견 보내기 · 로그아웃.
export const RailMember = ({ member, onLogout, className }: Pick<Props, "member" | "onLogout" | "className">) => {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  return (
    <div className={cn("flex items-center gap-x-2 border-t border-rail-border px-2 pt-3", className)} data-testid="rail-member">
      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-rail-active text-xs font-semibold text-rail-strong">
        {member.name[0]}
      </span>
      <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-xs text-rail-foreground">
        {member.name} · {ROLE_LABEL[member.role]}
      </span>
      <RailIconButton aria-label="의견 보내기" data-testid="feedback-open" title="의견 보내기" onClick={() => setFeedbackOpen(true)}>
        <MessageSquarePlus className="size-4" />
      </RailIconButton>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <form action={onLogout}>
        <RailIconButton aria-label="로그아웃" type="submit">
          <LogOut className="size-4" />
        </RailIconButton>
      </form>
    </div>
  );
};

const AppRail = ({
  member,
  onLogout,
  orgName,
  plan,
  badges,
  className,
  onNavigate,
}: Props) => {
  return (
    <div
      className={cn(
        // 다크에서는 레일과 작업면의 명도가 거의 같아 경계가 사라진다. 본문을 내려도 로고·사용자 줄은 제자리에 남고 메뉴만 안에서 넘친다 · 상태바·홈 표시줄·가로 노치만큼 안쪽으로(앱 껍데기).
        "flex w-52 shrink-0 flex-col border-r border-rail-border bg-rail pb-safe-2.5 pl-safe-2.5 pr-2.5 pt-safe-3.5 sticky top-0 h-screen",
        className
      )}>
      <Link className="flex shrink-0 items-center gap-x-2.5 px-2 pb-2 pt-1" href="/app">
        <BrandIcon decorative tone="rail" />
        <span className="flex min-w-0 flex-col gap-y-0.5">
          <BrandWordmark className="h-4 self-start" tone="rail" />
          {orgName && (
            <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-rail-muted" data-testid="rail-org-name">
              {orgName}
            </span>
          )}
          {plan && (
            <span className="text-xs text-rail-muted" data-testid="rail-plan">
              {PLAN_LABEL[plan]}
            </span>
          )}
        </span>
      </Link>

      <div className="-mx-2.5 min-h-0 flex-1 overflow-y-auto px-2.5">
        <AppNavList badges={badges} onNavigate={onNavigate} />
      </div>

      <RailMember className="mt-2 shrink-0" member={member} onLogout={onLogout} />
    </div>
  );
};

export default AppRail;
